import React, { useEffect, useState } from 'react'
import { Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, map } from 'ramda'
import { useTranslation } from 'react-i18next'
import { getOptions, getUserValue } from '../utils'
import {
  fetchAccountType,
  fetchAnnualIncome,
  fetchCommissionTypes,
  fetchDepositPerAnnum,
  fetchDistributionTypes,
  fetchHighestLevelEducation,
  fetchPersonTitle,
  fetchPortfolioSize,
  fetchPurposeAccountOpen
} from '../../api'
import { usePresetValue } from '../../utils/hooks'

const selectPropsByName = {
  personTitle: {
    isSendDataAsNumber: true,
    fetchFunction: fetchPersonTitle
  },
  highestLevelEducation: {
    isSendDataAsNumber: true,
    fetchFunction: fetchHighestLevelEducation
  },
  portfolioSizeInEur: {
    isSendDataAsNumber: true,
    fetchFunction: fetchPortfolioSize
  },
  depositPerAnnum: {
    isSendDataAsNumber: true,
    fetchFunction: fetchDepositPerAnnum
  },
  purposeAccountOpen: {
    isSendDataAsNumber: true,
    fetchFunction: fetchPurposeAccountOpen
  },
  annualIncomeId: {
    isSendDataAsNumber: true,
    fetchFunction: fetchAnnualIncome
  },
  accountClassifyId: {
    isSendDataAsNumber: true,
    fetchFunction: fetchAccountType
  },
  fundDistType: {
    isSendDataAsNumber: false,
    fetchFunction: fetchDistributionTypes
  },
  commissionType: {
    isSendDataAsNumber: false,
    fetchFunction: fetchCommissionTypes
  }
}

/**
 * @param {string} label
 * @param {string} name
 * @param {string} additionalInfo
 */
const ExternalSelect = ({ label, name, additionalInfo }) => {
  const { t, i18n } = useTranslation()
  const [value, setValue] = useState('')
  const [list, setList] = useState([])
  const [isValid, setIsValid] = useState(null)
  const [{ user, form: { accId } }, dispatch] = useStore()
  const userValue = getUserValue(accId, user, name)

  useEffect(() => {
    /**
     * @param {OptionsInput} data
     */
    const fetchCallback = (data) => {
      setList(getOptions(data))
    }
    const fetchData = selectPropsByName[name]?.fetchFunction
    if (typeof fetchData === 'function') {
      fetchData().then(fetchCallback)
    }
  }, [i18n.language, name])

  useEffect(() => {
    if (name !== 'purposeAccountOpen') return
    const curItem = list.find(item => item.value.toString() === value)
    dispatch({ type: 'setExtFieldValue', value: curItem?.showDescription, name: 'showPurposeAccountDescription' })
  }, [value, list])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  /**
   * @param {(string|number)} value
   */
  const presetValue = (value) => {
    const stringValue = value.toString()
    setValue(stringValue)
    const isValid = stringValue.length > 0
    setIsValid(isValid)
    dispatch({
      type: 'setExtFieldValue',
      name,
      value: selectPropsByName[name]?.isSendDataAsNumber ? +value : value
    })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  usePresetValue(name, userValue, presetValue)

  /**
   * @param {{target: {value: string, name: string}}} props
   */
  const onChange = ({ target: { value, name } }) => {
    const selectedValue = getSelectValue(value)
    setValue(selectedValue)
    setIsValid(selectedValue.length > 0)
    dispatch({
      type: 'setExtFieldValue',
      name,
      value: selectPropsByName[name]?.isSendDataAsNumber ? +selectedValue : selectedValue
    })
    dispatch({ type: 'setValidationFld', name, value: selectedValue.length > 0 })
  }

  return (
    <Select
      value={value || ''}
      label={t(label)}
      placeholder={t('generatedField.choose')}
      options={list}
      onChange={onChange}
      name={name}
      key={name}
      error=''
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
    />
  )
}

const getExternalSelectFields = map(currencyField => {
  const { i18n, name, required, order, dependOnBooleanFldName } = currencyField
  return (
    <ExternalSelect
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      key={name}
      order={order}
      dependOnBooleanFldName={dependOnBooleanFldName}
    />
  )
})

export { ExternalSelect, getExternalSelectFields }
